import React, { useCallback, useContext } from 'react'

import {
  Button, Chat2, classnames, FavoriteFilled, FavoriteOutlined, Icon,
} from '@cgtrader/triangle-ui/dist/src/ssr-index'

import usePageConfig from 'Pods/Shared/Hooks/usePageConfig'
import { iconSize } from 'Pods/ThreeDStudio/constants'
import useTranslation from 'Pods/ThreeDStudio/Hooks/useTranslation'
import { useThreeDStudioStore } from 'Pods/ThreeDStudio/Store'
import { openCommentsModal, openSignInModal } from 'Pods/ThreeDStudio/Store/actions/ui'
import ThreeDStudioPageContext from 'Pods/ThreeDStudio/ThreeDStudioPageContext'

import useModelLike from '../../Hooks/useModelLike'
import useModelLikers from '../../Hooks/useModelLikers'
import LikersStack from './Components/LikersStack/LikersStack'

// Like and comment actions for the opened model, rendered for every model (the
// viewer's own and community ones alike). The seed counts ride on openedModel
// (the community serializers expose likeCount and commentsCount); the viewer's
// own like state is user-specific and fetched client-side by useModelLike.
function EngagementActions() {
  const t = useTranslation()
  const { userLoggedIn, pageConfigLoaded } = useContext(ThreeDStudioPageContext)
  const slug = useThreeDStudioStore(state => state.ui?.openedModel?.slug)
  const initialCount = useThreeDStudioStore(state => state.ui?.openedModel?.likeCount) ?? 0
  const commentsCount = useThreeDStudioStore(state => state.ui?.openedModel?.commentsCount) ?? 0

  const {
    isLiked, likeCount, isToggling, toggleLike,
  } = useModelLike({ slug, initialCount, dailyLimitMessage: t('like.dailyLimitReached') })

  // Flag-gated (:enable_ai_model_likers). Enabling per actor means signed-out
  // visitors never see the stack while the flag is rolling out.
  const likersEnabled = !!usePageConfig()?.aiModelLikersEnabled

  const {
    likers, totalCount, hasMore, isLoading: isLoadingLikers, loadMore,
  } = useModelLikers({
    slug, likeCount, isToggling, enabled: likersEnabled,
  })

  const handleLike = useCallback(() => {
    // On this fast page the auth state is unknown until /load-services.js
    // resolves, and `userLoggedIn` is false during that window. Ignore the click
    // until it has loaded so a signed-in user isn't wrongly prompted to sign in.
    if (!pageConfigLoaded) return

    // POST/DELETE answer 401 for anonymous visitors, so prompt instead of firing.
    if (!userLoggedIn) {
      openSignInModal()

      return
    }

    toggleLike()
  }, [pageConfigLoaded, userLoggedIn, toggleLike])

  const handleComments = useCallback(() => {
    openCommentsModal()
  }, [])

  if (!slug) {
    return null
  }

  // The stack waits for the first page: an empty box would occupy width it
  // cannot fill, and shift the row a second time once the faces arrive.
  const showLikers = likersEnabled && likers.length > 0

  return (
    <div className="footer-actions__engagement">
      <div className={classnames('footer-actions__likes', showLikers && 'footer-actions__likes--with-stack')}>
        <Button
          icon={<Icon svgComponent={isLiked ? FavoriteFilled : FavoriteOutlined} size={iconSize} notSpaced />}
          type="secondary"
          size="small"
          variant="with-icon"
          actionType="button"
          customClass="footer-actions__like-toggle"
          aria-label={isLiked ? t('like.unlike') : t('like.like')}
          aria-pressed={isLiked}
          onClick={handleLike}
          disabled={isToggling}
        >
          {likeCount}
        </Button>
        {showLikers && (
          <LikersStack
            likers={likers}
            totalCount={totalCount}
            hasMore={hasMore}
            isLoading={isLoadingLikers}
            onLoadMore={loadMore}
          />
        )}
      </div>
      <Button
        icon={<Icon svgComponent={Chat2} color="darker" size={iconSize} notSpaced />}
        type="secondary"
        size="small"
        variant="with-icon"
        aria-label={t('commentsModal.commentsCount', { count: commentsCount })}
        onClick={handleComments}
      >
        {commentsCount}
      </Button>
    </div>
  )
}

export default EngagementActions
